import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useMediaQuery } from "react-responsive";
import classNames from "classnames";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { Icon } from "../../components/Icon";
import { useIsClient } from "../../hooks/useIsClient/useIsClient";
import { usePlatformDetector } from "../../hooks/usePlatformDetector";
import Replay from "../../icons/back.svg";
import FullscreenCloseIcon from "../../icons/fullscreen-close.svg";
import FullscreenOpenIcon from "../../icons/fullscreen-open.svg";
import PlayIcon from "../../icons/play-icon.svg";
import SoundMute from "../../icons/sound-mute.svg";
import SoundOn from "../../icons/sound-on.svg";
import { useConfig } from "../../providers/useConfig";
import { useTranslations } from "../../providers/useTranslations";
import { formatTimeline } from "../../utils/formatTime/formatTime";
import { exitFullscreen, fullscreenEnabled, isFullscreen, requestFullscreen, requestFullscreenMobileIOS, } from "../../utils/fullscreenHelpers/fullscreenHelpers";
import { VideoControls } from "./components/VideoControls";
import { VideoPlayerInfo } from "./components/VideoPlayerInfo";
import { VideoPlayerPoster } from "./components/VideoPlayerPoster";
import { getFileName } from "./helpers/helpers";
import styles from "./VideoPlayer.module.scss";
export const VideoPlayer = React.memo(({ desktopSrc = "", tabletSrc = "", mobileSrc = "", hostedExternally = false, externalLinkVideo, loop = false, autoPlay = false, poster = "", alt = "", title = "", description, playLabel, replayLabel, soundOnLabel, muteLabel, showFullscreenLabel, preventDownload, showDuration = false, showControls = false, preventMainPlayButtonClick = false, showFullscreen = false, className, contentSize = false, preload = false, removeRoundedCorners, keepOriginalAspectRatio = false, squareContainer = false, shouldStopVideo = false, themeClass = "page--dark", videoClassName, setIsVideoLoaded, isFirstElement, }) => {
    const { __ } = useTranslations();
    const config = useConfig();
    const { isClient } = useIsClient();
    const isPreventDownload = preventDownload ?? (config.blockVideoDownload || false);
    const isShowControls = showControls ?? (config.showVideoControls || false);
    const isDesktop = useMediaQuery({ query: "(min-width: 960px)" });
    const isTablet = useMediaQuery({ query: "(min-width: 480px) and (max-width: 959px)" });
    const isMobile = useMediaQuery({ maxWidth: 479 });
    const playerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [played, setPlayed] = useState(0);
    const [volume, setVolume] = useState(1);
    const [duration, setDuration] = useState(0);
    const [muted, setMuted] = useState(autoPlay);
    const [isPosterActive, setIsPosterActive] = useState(false);
    const videoWrapperRef = useRef(null);
    const [videoEnded, setVideoEnded] = useState(false);
    const [videoSrc, setVideoSrc] = useState("");
    const [fullScreenOpenIcon, setFullScreenOpenIcon] = useState(true);
    const stopGTMEvent = loop && autoPlay && !showControls;
    useEffect(() => {
        const isPosterNonEmpty = poster.length > 0;
        setIsPosterActive(isPosterNonEmpty && !autoPlay);
    }, [poster, autoPlay]);
    useEffect(() => {
        if (hostedExternally && externalLinkVideo && externalLinkVideo.length > 0) {
            setVideoSrc(externalLinkVideo);
        }
        else if (isMobile) {
            setVideoSrc(mobileSrc || tabletSrc || desktopSrc);
        }
        else if (isTablet) {
            setVideoSrc(tabletSrc || desktopSrc);
        }
        else if (isDesktop) {
            setVideoSrc(desktopSrc);
        }
    }, [hostedExternally, externalLinkVideo, mobileSrc, tabletSrc, desktopSrc, isMobile, isTablet, isDesktop]);
    useEffect(() => {
        if (isPosterActive && isPlaying) {
            setIsPosterActive(false);
        }
    }, [isPlaying, isPosterActive]);
    useEffect(() => {
        if (shouldStopVideo) {
            setIsPlaying(false);
        }
    }, [shouldStopVideo]);
    const platform = usePlatformDetector();
    const iosPlatform = platform === "ios";
    const toggleFullscreen = (event) => {
        event.stopPropagation();
        const video = videoWrapperRef.current;
        const videoTag = video.querySelector("video");
        const isIOSMobileView = isMobile && iosPlatform;
        const isFullScreenDisabled = isIOSMobileView ? false : !fullscreenEnabled();
        if (!video || isFullScreenDisabled)
            return;
        if (isFullscreen()) {
            setFullScreenOpenIcon(true);
            exitFullscreen();
        }
        else {
            setFullScreenOpenIcon(false);
            isIOSMobileView ? requestFullscreenMobileIOS(videoTag) : requestFullscreen(video);
        }
    };
    const dispatchPlayGTMEvent = () => {
        dispatchTrackingEvent(TrackingEvents.VideoClick, { video_title: title || getFileName(desktopSrc) });
    };
    const dispatchStopGTMEvent = () => {
        let watchTime = Math.round(duration * played * 100) / 100;
        let completion = Math.round(played * 100);
        const isShortVideoAboutToEnd = duration <= 15 && completion >= 85;
        if (isShortVideoAboutToEnd) {
            completion = 100;
            watchTime = Math.round(duration * 100) / 100;
        }
        dispatchTrackingEvent(TrackingEvents.VideoStop, {
            video_title: title || getFileName(desktopSrc),
            video_watchtime: watchTime,
            video_completion: completion,
        });
    };
    const handlePlayPause = useCallback(() => {
        if (hostedExternally)
            return false;
        setIsPlaying((prevIsPlaying) => {
            if (videoEnded && !loop) {
                setVideoEnded(false);
                return true;
            }
            return !prevIsPlaying;
        });
        if (isPosterActive) {
            setIsPosterActive(false);
        }
    }, [videoEnded, loop, isPosterActive, hostedExternally]);
    useEffect(() => {
        const shouldAutoPlay = autoPlay && !hostedExternally;
        if (shouldAutoPlay) {
            setIsPlaying(true);
        }
    }, [autoPlay, hostedExternally]);
    const handleProgress = ({ played }) => {
        setPlayed(played);
    };
    const handleSeekChange = (e) => {
        setPlayed(parseFloat(e.target.value));
    };
    const handleSeekMouseUp = (e) => {
        const isTouchEvent = "changedTouches" in e;
        const inputElement = isTouchEvent
            ? e.changedTouches[0].target
            : e.currentTarget;
        const value = parseFloat(inputElement.value);
        if (playerRef.current) {
            playerRef.current.seekTo(value);
        }
    };
    const handleDuration = (duration) => {
        setDuration(duration);
    };
    const handleVolumeChange = useCallback((e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (newVolume <= 0) {
            setMuted(true);
            setVolume(newVolume);
        }
        else {
            setMuted(false);
        }
    }, []);
    const handleMute = (event) => {
        event.stopPropagation();
        setMuted((muted) => !muted);
    };
    const preventContextMenu = (event) => {
        if (isPreventDownload) {
            event.preventDefault();
        }
    };
    const handleEnd = () => {
        setVideoEnded(true);
        setIsPlaying(false);
    };
    const handleStart = () => {
        setVideoEnded(false);
    };
    const handlePlay = () => {
        if (!stopGTMEvent) {
            dispatchPlayGTMEvent();
        }
    };
    const playerConfig = hostedExternally
        ? {
            youtube: {
                playerVars: {
                    rel: 0,
                    autoplay: 0,
                    controls: 1,
                },
            },
        }
        : !preload
            ? {
                file: {
                    attributes: {
                        preload: "metadata",
                    },
                },
            }
            : {};
    const handleMainPlayButtonClick = useCallback((event) => {
        if (preventMainPlayButtonClick)
            return;
        setIsPosterActive(false);
        const isClickWithinControls = event.target instanceof Element && event.target.closest('div[class*="videoPlayerControls"]');
        if (!isClickWithinControls) {
            handlePlayPause();
        }
    }, [handlePlayPause, preventMainPlayButtonClick]);
    const renderMainPlayIcon = () => {
        return (_jsx("button", { "aria-label": playLabel, className: classNames(styles.videoPlayerPlayIcon), "data-testid": "videPlayerPosterIcnBtn", children: _jsx(Icon, { className: styles.iconPlay, component: PlayIcon, height: "2rem", width: "2rem" }) }));
    };
    const renderReplayIcon = () => {
        return (_jsx("button", { "aria-label": replayLabel, className: classNames(styles.videoPlayerPlayIcon), "data-testid": "videoPlayerReplayButton", children: _jsx(Icon, { className: styles.iconReplay, component: Replay }) }));
    };
    const videoPlayerWrapperClassName = classNames("videoPlayerWrapper", className, themeClass, {
        [styles.videoPlayerWrapperNosrc]: desktopSrc.length === 0,
        [styles.videoPlayerWrapperExternal]: hostedExternally,
        [styles.videoPlayerWrapperFullscreen]: !fullScreenOpenIcon,
        [styles.videoPlayerWrapperContentSize]: contentSize,
        [styles.videoPlayerWrapperSquare]: squareContainer,
        [styles.noRoundedCorners]: removeRoundedCorners,
    });
    const handleVideoPause = () => {
        if (!stopGTMEvent) {
            dispatchStopGTMEvent();
        }
    };
    const handleLoad = useCallback(() => {
        if (isFirstElement && setIsVideoLoaded) {
            setIsVideoLoaded(true);
        }
    }, [isFirstElement, setIsVideoLoaded]);
    const fullScreenIcon = isMobile && iosPlatform ? FullscreenOpenIcon : fullScreenOpenIcon ? FullscreenOpenIcon : FullscreenCloseIcon;
    return (_jsxs("div", { className: classNames("videoContainer", videoClassName, styles.videoPlayerContainer), children: [_jsxs("div", { ref: videoWrapperRef, onClick: handleMainPlayButtonClick, className: videoPlayerWrapperClassName, "data-testid": "video-element", dir: "ltr", children: [!isShowControls && !hostedExternally && !isPlaying && !videoEnded && renderMainPlayIcon(), !isShowControls && hostedExternally && isPosterActive && renderMainPlayIcon(), !isShowControls && !hostedExternally && videoEnded && renderReplayIcon(), isPosterActive && _jsx(VideoPlayerPoster, { poster: poster, alt: alt, title: title }), isShowControls && !hostedExternally && (_jsx("div", { className: styles.videoPlayerViewportIconsWrapper, children: _jsx("div", { className: styles.videoPlayerSound, children: _jsx("button", { "aria-label": !muted ? soundOnLabel : muteLabel, className: classNames(styles.videoPlayerButton), onClick: handleMute, "data-testid": "videoControlsMuteButton", children: _jsx(Icon, { className: styles.iconSpeaker, component: !muted ? SoundOn : SoundMute }) }) }) })), !hostedExternally && showFullscreen && (_jsx("button", { "aria-label": showFullscreenLabel, className: styles.videoPlayerFullscreenButton, "data-testid": "videoPlayerFullscreenButton", onClick: toggleFullscreen, type: "button", children: _jsx(Icon, { className: classNames(styles.iconFullscreen), component: fullScreenIcon }) })), _jsx("div", { className: classNames("videoPlayerAspectRatioBox", {
                            [styles.videoPlayerKeepOriginalAspectRatio]: keepOriginalAspectRatio,
                            [styles.noRoundedCorners]: removeRoundedCorners,
                        }), children: isClient && (_jsx(ReactPlayer, { suppressHydrationWarning: true, url: videoSrc, ref: playerRef, playing: isPlaying, volume: volume, width: "100%", height: "100%", onProgress: handleProgress, onDuration: handleDuration, onEnded: handleEnd, onStart: handleStart, onPlay: handlePlay, loop: loop, muted: muted, config: playerConfig, onPause: handleVideoPause, playsinline: true, onReady: handleLoad })) }), isPreventDownload && !hostedExternally && (_jsx("div", { onContextMenu: preventContextMenu, className: styles.videoPlayerDownloadLayer })), showControls && !hostedExternally && (_jsx(VideoControls, { onPlayPause: handlePlayPause, playing: isPlaying, played: played, onSeekChange: handleSeekChange, onSeekMouseUp: handleSeekMouseUp, onVolumeChange: handleVolumeChange, elapsedTime: formatTimeline(played * duration), totalDuration: formatTimeline(duration), onMute: handleMute, volume: volume, muted: muted, loop: loop, videoEnded: videoEnded }))] }), _jsx(VideoPlayerInfo, { duration: duration, showDuration: showDuration, description: description })] }));
});
//# sourceMappingURL=VideoPlayer.js.map